import { PrintMarkerClip } from './print/PrintMarkerClip'
import { useMemo } from 'react'
import { GeoJsonFillLayer, GeoJsonLineLayer, MapMarker } from '@wehunt/map'
import {
  groundFeatureCollection,
  groundHaloOffsetExpression,
  groundWidthExpression,
  lineDashPattern,
  lineFeatureCollection,
  lineWidthExpression,
  segmentBearingDeg,
  washFeatureCollection,
} from '../lib/mapGeoJson'
import {
  FILLED_WASH_FILL,
  FORBIDDEN_FILL,
  FORBIDDEN_STROKE,
  GROUND_BASE_WIDTH,
  GROUND_HALO_OPACITY,
  REVERSE_WASH_FILL,
  SUBAREA_STROKE,
  SUBAREA_STROKE_OPACITY,
  groundShapes,
  huntOutlineWidths,
  polygonCentroidOfRings,
  shouldDrawGroundHalo,
} from '../lib/mapBorders'
import { filledWashPolygons, reverseWashPolygons } from '../lib/groundWash'
import { LINE_BORDER_FACTOR, LINE_DEFAULT_COLOR, MAPLIBRE_LAW_ZOOM_OFFSET, lineStrokeWidthCssPx } from '../lib/mapLines'
import { isLineKindVisible, type MapAppearance } from '../lib/mapAppearance'
import type { GeoLine, GeoLineStyle, GeoPt, TeamMap } from '../lib/types'
import { LineNameMarker } from './LineNameMarker'

export interface TeamMapLayersProps {
  map: TeamMap
  appearance: MapAppearance
  /** Live zoom (labels gate ≥ 10; arrowhead size). */
  zoom: number
  /** Opt out of the sub-area label markers while leaving the `sub` polygon
   *  layer's own visibility unchanged (it still follows
   *  `appearance.subAreasVisible`). Defaults to `true`. Read-only embeds (the
   *  hunt report map) draw the sub-areas but not their labels, matching the
   *  surface they replaced. */
  subAreaLabels?: boolean
  /**
   * CLUB-676: lines the viewer has hidden from the pin collection, which are
   * not drawn — neither the stroke nor the arrowhead marker at its end.
   *
   * Only the drawn lines are filtered here: pins live on the surface above
   * this one (`TeamMapView` renders their markers), so the same hidden set is
   * applied in two places rather than one.
   */
  hiddenObjectIds?: ReadonlySet<string>
  lineLabels?: boolean
  printMode?: boolean
  dimOutside?: GeoPt[] | null
}

/** Sub-area labels only show at or above this zoom — below it a hunting
 *  ground's sub-areas are too small on screen for a name to be legible. */
const SUBAREA_LABEL_MIN_ZOOM = 10

/** Every drawn-line style, in the fixed order the layer stack renders them —
 *  order never changes, so casing/interior pairing stays a static tree shape
 * (see the file-level note on why every style renders unconditionally). */
const LINE_STYLES: readonly GeoLineStyle[] = ['solid', 'dashed', 'dotted', 'dash_dot']

/** The arrowhead's half-opening-angle, matching `arrowHeadPath`'s own
 *  `tan(Math.PI / 8)` constant (22.5°) — the same triangle geometry, just
 *  derived the other way around: `arrowHeadPath` is handed a base width and
 *  derives a height; here the marker's height is fixed by the stroke width
 *  and the base width is derived from it. */
const ARROW_HALF_ANGLE = Math.PI / 8

/** The app's screen-space cap on an arrowhead's size (WET-4184 parity). */
const ARROW_MAX_HEIGHT = 56

/** True when two points are the same coordinate — used to walk back past a
 *  duplicated final point when deriving an arrowhead's direction. */
function samePoint(a: GeoPt, b: GeoPt): boolean {
  return a.lat === b.lat && a.lon === b.lon
}

/** The point to derive an arrowhead's direction from: the last point in
 *  `points` distinct from the final one, walking backward past any
 *  duplicates. `null` when every point coincides with the last (no direction
 *  to draw). Mirrors `arrowHeadPath`'s own backward walk. */
function secondLastDistinctPoint(points: readonly GeoPt[]): GeoPt | null {
  const last = points[points.length - 1]
  for (let i = points.length - 2; i >= 0; i--) {
    if (!samePoint(points[i], last)) return points[i]
  }
  return null
}

/** A filled triangle pointing north (up), pre-rotation — the marker rotates
 *  it to the line's bearing. `height` is the caller-resolved, already-capped
 *  arrowhead height; the base width follows from the 22.5° opening angle. */
function ArrowHeadGlyph({ color, height }: { color: string; height: number }) {
  const width = 2 * height * Math.tan(ARROW_HALF_ANGLE)
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: 'block', overflow: 'visible' }}
    >
      <polygon
        points={`${width / 2},0 0,${height} ${width},${height}`}
        fill={color}
        stroke="#000"
        strokeWidth={1}
      />
    </svg>
  )
}

/** Ground + drawn-line layers for one team map, rendered through
 *  `@wehunt/map`. Layer ids and their bottom-to-top order are fixed: `MapView`
 *  anchors the raster basemap below the first `gj-` layer, and every layer
 *  below passes no `beforeId`, so render order IS paint order —
 *  `wash`, `forbidden-fill`, `forbidden-line`, `sub`, `hunt-halo`, `hunt`. */
export function TeamMapLayers({
  map,
  appearance,
  zoom,
  subAreaLabels = true,
  hiddenObjectIds,
  lineLabels = false,
  printMode = false,
  dimOutside,
}: TeamMapLayersProps): JSX.Element {
  const lt = appearance.lineThickness

  const { arrowsVisible, linesVisible } = appearance
  // The layer switches drop a line here rather than hiding a layer, because an
  // arrow and a line share the per-style layers; the arrowhead markers read from
  // this list too, so a hidden arrow loses its head along with its stroke.
  const drawnLines = useMemo(
    () => (map.lines ?? []).filter(
      (l) => !hiddenObjectIds?.has(l.id) && isLineKindVisible({ arrowsVisible, linesVisible }, l.kind),
    ),
    [map.lines, hiddenObjectIds, arrowsVisible, linesVisible],
  )

  const forbiddenFC = useMemo(
    () => groundFeatureCollection(map.forbiddenPolygons, 'forbidden'),
    [map.forbiddenPolygons],
  )
  const subareaFC = useMemo(
    () => groundFeatureCollection(map.subAreaPolygons, 'subarea'),
    [map.subAreaPolygons],
  )
  const huntFC = useMemo(
    () => groundFeatureCollection(map.boundaryAreas, 'hunt', { teamColor: map.borderColor }),
    [map.boundaryAreas, map.borderColor],
  )
  const linesFC = useMemo(
    () => lineFeatureCollection(drawnLines, { lines: lt.lines, arrows: lt.arrows }),
    [drawnLines, lt.lines, lt.arrows],
  )
  const subareaShapes = useMemo(
    () => groundShapes(map.subAreaPolygons, 'subarea'),
    [map.subAreaPolygons],
  )

  const borderStyle = appearance.borderAppearance
  const forbiddenShown = appearance.forbiddenAreasVisible
  // The wash is heavy geometry (a union, or an ellipse fit and a difference per
  // cluster), so it recomputes only when the areas or the style change — never
  // on zoom or on an unrelated appearance switch.
  //
  // A hidden forbidden area is not cut out of the filled wash. Leaving the hole
  // drew the area's exact outline in the green, so switching the layer off still
  // showed it — which reads as the switch not working. This departs from the app,
  // whose filled fill keeps the cut-out whatever the switch says.
  const washFC = useMemo(() => {
    if (borderStyle === 'standard') return washFeatureCollection([])
    const polys = borderStyle === 'reverse'
      ? reverseWashPolygons(map.boundaryAreas ?? [])
      : filledWashPolygons(map.boundaryAreas ?? [], forbiddenShown ? (map.forbiddenPolygons ?? []) : [])
    return washFeatureCollection(polys)
  }, [borderStyle, map.boundaryAreas, map.forbiddenPolygons, forbiddenShown])
  const outline = huntOutlineWidths(borderStyle)
  // The app withholds the standard halo at low zoom; under a wash the band is
  // part of the outline and always drawn.
  const showBand = borderStyle !== 'standard' || shouldDrawGroundHalo(zoom)

  const forbiddenVisibility = forbiddenShown ? 'visible' : 'none'

  const showSubareaLabels =
    subAreaLabels && (printMode || zoom >= SUBAREA_LABEL_MIN_ZOOM) && appearance.subAreasVisible

  const arrowLines: GeoLine[] = drawnLines.filter(
    (line) => line.kind === 'arrow' && line.points.length >= 2,
  )

  return (
    <>
      {/* Always mounted, empty for `standard`: a layer added on a later style
          switch would be appended ABOVE the outlines, since render order is
          paint order here and nothing passes `beforeId`. */}
      <GeoJsonFillLayer
        id="wash"
        data={washFC}
        paint={{ 'fill-color': borderStyle === 'reverse' ? REVERSE_WASH_FILL : FILLED_WASH_FILL }}
      />
      {/* Hidden by layout rather than unmounted, for the same paint-order reason
          as the wash above. */}
      <GeoJsonFillLayer
        id="forbidden-fill"
        data={forbiddenFC}
        paint={{ 'fill-color': FORBIDDEN_FILL }}
        layout={{ visibility: forbiddenVisibility }}
      />
      <GeoJsonLineLayer
        id="forbidden-line"
        data={forbiddenFC}
        paint={{
          'line-color': FORBIDDEN_STROKE,
          'line-width': groundWidthExpression(GROUND_BASE_WIDTH.forbidden, lt.forbidden),
        }}
        layout={{ visibility: forbiddenVisibility }}
      />
      <GeoJsonLineLayer
        id="sub"
        data={subareaFC}
        paint={{
          'line-color': SUBAREA_STROKE,
          'line-opacity': SUBAREA_STROKE_OPACITY,
          'line-width': groundWidthExpression(GROUND_BASE_WIDTH.subarea, lt.subarea),
        }}
        layout={{ visibility: appearance.subAreasVisible ? 'visible' : 'none' }}
      />
      {/* The translucent band OUTSIDE the edge, the app's `StrokePosition.outside`
          halo: beyond the edge on an outer ring, into the hole on a hole. The
          offset's sign is not readable from the ring winding — see
          `groundHaloOffsetExpression`, which owns that reasoning and the negation. */}
      <GeoJsonLineLayer
        id="hunt-halo"
        data={huntFC}
        paint={{
          'line-color': ['get', 'color'],
          'line-opacity': GROUND_HALO_OPACITY,
          'line-width': groundWidthExpression(outline.band, lt.border),
          'line-offset': groundHaloOffsetExpression(outline.band, lt.border),
        }}
        layout={{ visibility: showBand ? 'visible' : 'none' }}
      />
      <GeoJsonLineLayer
        id="hunt"
        data={huntFC}
        paint={{
          'line-color': ['get', 'color'],
          'line-width': groundWidthExpression(outline.core, lt.border),
        }}
      />

      {LINE_STYLES.map((style) => {
        // The casing draws LINE_BORDER_FACTOR wider than the interior, and a
        // MapLibre dasharray unit is a multiple of the LAYER's OWN
        // line-width — so the casing's pattern must be divided by that same
        // factor to land on the same physical dash/gap length as the
        // interior's (see lineDashPattern's own doc comment).
        const casingDash = lineDashPattern(style, LINE_BORDER_FACTOR)
        const interiorDash = lineDashPattern(style)
        const filter = ['==', ['get', 'style'], style]
        const layout = { 'line-cap': 'butt', 'line-join': 'miter' }
        return [
          <GeoJsonLineLayer
            key={`${style}-casing`}
            id={`lines-${style}-casing`}
            data={linesFC}
            paint={{
              'line-color': '#000',
              'line-width': lineWidthExpression(LINE_BORDER_FACTOR),
              ...(casingDash ? { 'line-dasharray': casingDash } : {}),
            }}
            layout={layout}
            filter={filter}
          />,
          <GeoJsonLineLayer
            key={style}
            id={`lines-${style}`}
            data={linesFC}
            paint={{
              'line-color': ['get', 'color'],
              'line-width': lineWidthExpression(),
              ...(interiorDash ? { 'line-dasharray': interiorDash } : {}),
            }}
            layout={layout}
            filter={filter}
          />,
        ]
      })}

      {arrowLines.map((line) => {
        const from = secondLastDistinctPoint(line.points)
        if (!from) return null
        const to = line.points[line.points.length - 1]
        const height = Math.min(lineStrokeWidthCssPx(line.width, zoom + MAPLIBRE_LAW_ZOOM_OFFSET, lt.arrows) * 5, ARROW_MAX_HEIGHT)
        return (
          <MapMarker key={line.id} lat={to.lat} lon={to.lon} anchor="center" rotation={dimOutside ? undefined : segmentBearingDeg(from, to)}>
            <PrintMarkerClip point={to} frame={dimOutside}><div style={{ transform: dimOutside ? `rotate(${segmentBearingDeg(from, to)}deg)` : undefined }}><ArrowHeadGlyph color={line.color ?? LINE_DEFAULT_COLOR} height={height} /></div></PrintMarkerClip>
          </MapMarker>
        )
      })}

      {lineLabels && drawnLines.map((line) => <LineNameMarker key={`${line.id}-name`} line={line} zoom={zoom} appearance={appearance} dimOutside={dimOutside} />)}
      {showSubareaLabels &&
        subareaShapes.map((shape) => {
          if (!shape.name) return null
          const centroid = polygonCentroidOfRings(shape.rings)
          if (!centroid) return null
          return (
            <MapMarker key={shape.key} lat={centroid.lat} lon={centroid.lon} anchor="center">
              <PrintMarkerClip point={centroid} frame={dimOutside}><span
                style={{
                  fontWeight: 700,
                  fontFamily: 'Geist, ui-sans-serif, system-ui, sans-serif',
                  color: '#fff',
                  whiteSpace: 'nowrap',
                  textShadow: '0 0 2px #000, 0 0 2px #000, 0 0 2px #000',
                }}
              >
                {shape.name.toUpperCase()}
              </span></PrintMarkerClip>
            </MapMarker>
          )
        })}
    </>
  )
}
